import apiClient from '@/lib/api-client'
import type {
  BookCategory,
  BookListResponse,
  BookWithContent,
  ContextPosition,
  ImportResumeSkillsResponse,
  InitializeLearningResponse,
  LearningModule,
  LearningNote,
  LearningQA,
  LearningQAListResponse,
  LearningTopicDetail,
  LearningTopicListResponse,
  LearningTopicWithProgress,
  OverallLearningProgress,
  PodcastCategory,
  PodcastListResponse,
  PodcastWithContent,
  RecommendedTopicsResponse,
  RefreshContextResponse,
  SkillProfile,
  SkillProfileUpdate,
  UserLearningProgress,
  UserLearningProgressUpdate,
  WeeklyProgress,
} from '@/types'

export const learningService = {
  async getProfile(): Promise<SkillProfile> {
    const response = await apiClient.get<SkillProfile>('/api/v1/learn/profile')
    return response.data
  },

  async updateProfile(data: SkillProfileUpdate): Promise<SkillProfile> {
    const response = await apiClient.patch<SkillProfile>('/api/v1/learn/profile', data)
    return response.data
  },

  async initializeLearning(): Promise<InitializeLearningResponse> {
    const response = await apiClient.post<InitializeLearningResponse>('/api/v1/learn/initialize')
    return response.data
  },

  async listTopics(
    page = 1,
    limit = 50,
    include_popular = true,
  ): Promise<LearningTopicListResponse> {
    const response = await apiClient.get<LearningTopicListResponse>('/api/v1/learn/topics', {
      params: { page, limit, include_popular },
    })
    return response.data
  },

  async archiveJobTopics(jobPostingId: number): Promise<void> {
    await apiClient.delete(`/api/v1/learn/topics/by-job/${jobPostingId}`)
  },

  /** Archive a General / Explore topic (not tied to a job). Same semantics as job-path archive. */
  async archiveTopic(topicId: number): Promise<void> {
    await apiClient.delete(`/api/v1/learn/topics/${topicId}/archive`)
  },

  async getTopic(topicId: number): Promise<LearningTopicDetail> {
    const response = await apiClient.get<LearningTopicDetail>(`/api/v1/learn/topics/${topicId}`)
    return response.data
  },

  async getModule(moduleId: number): Promise<LearningModule> {
    const response = await apiClient.get<LearningModule>(`/api/v1/learn/modules/${moduleId}`)
    return response.data
  },

  async getProgress(): Promise<OverallLearningProgress> {
    const response = await apiClient.get<OverallLearningProgress>('/api/v1/learn/progress')
    return response.data
  },

  async updateProgress(
    moduleId: number,
    data?: UserLearningProgressUpdate,
  ): Promise<UserLearningProgress> {
    const response = await apiClient.post<UserLearningProgress>(
      `/api/v1/learn/progress/${moduleId}`,
      data,
    )
    return response.data
  },

  async getModuleProgress(moduleId: number): Promise<UserLearningProgress | null> {
    const response = await apiClient.get<UserLearningProgress | null>(
      `/api/v1/learn/progress/${moduleId}`,
    )
    return response.data
  },

  async getWeeklyProgress(): Promise<WeeklyProgress> {
    const response = await apiClient.get<WeeklyProgress>('/api/v1/learn/weekly-progress')
    return response.data
  },

  async getRecommended(limit = 5): Promise<RecommendedTopicsResponse> {
    const response = await apiClient.get<RecommendedTopicsResponse>('/api/v1/learn/recommended', {
      params: { limit },
    })
    return response.data
  },

  async refreshContext(): Promise<RefreshContextResponse> {
    const response = await apiClient.post<RefreshContextResponse>(
      '/api/v1/learn/profile/refresh-context',
    )
    return response.data
  },

  async importResumeSkills(): Promise<ImportResumeSkillsResponse> {
    const response = await apiClient.get<ImportResumeSkillsResponse>(
      '/api/v1/learn/profile/import-resume-skills',
    )
    return response.data
  },

  async generateTopic(
    skillName: string,
    options?: {
      currentLevel?: string
      targetLevel?: string
      timeAvailable?: string
      jobContext?: string
      jobPostingId?: number
    },
  ): Promise<LearningTopicWithProgress> {
    const response = await apiClient.post<LearningTopicWithProgress>('/api/v1/learn/generate', {
      skill_name: skillName,
      current_level: options?.currentLevel,
      target_level: options?.targetLevel,
      time_available: options?.timeAvailable,
      job_context: options?.jobContext,
      job_posting_id: options?.jobPostingId,
    })
    return response.data
  },

  async createFromJobMatch(request: {
    jobPostingId: number
    jobTitle: string
    companyName?: string
    jobLocation?: string
    skillGaps: Array<{
      skillName: string
      currentLevel?: string
      requiredLevel: string
      importance?: string
    }>
  }): Promise<{
    learningPathId: number | null
    topicsCreated: LearningTopicWithProgress[]
    message: string
  }> {
    const response = await apiClient.post('/api/v1/learn/from-job-match', {
      job_posting_id: request.jobPostingId,
      job_title: request.jobTitle,
      company_name: request.companyName,
      job_location: request.jobLocation,
      skill_gaps: request.skillGaps.map((gap) => ({
        skill_name: gap.skillName,
        current_level: gap.currentLevel,
        required_level: gap.requiredLevel,
        importance: gap.importance,
      })),
    })
    return {
      learningPathId: response.data.learning_path_id,
      topicsCreated: response.data.topics_created,
      message: response.data.message,
    }
  },

  // --- Audio Generation ---

  async generateAudio(
    topicId: number,
  ): Promise<{ status: string; modules_queued: number; module_ids: number[] }> {
    const response = await apiClient.post(`/api/v1/learn/topics/${topicId}/generate-audio`)
    return response.data
  },

  // --- Q&A (Ask during lesson) ---

  async askQuestion(
    moduleId: number,
    question: string,
    contextPosition?: ContextPosition,
  ): Promise<LearningQA> {
    const response = await apiClient.post<LearningQA>(
      `/api/v1/learn/modules/${moduleId}/questions`,
      {
        question,
        context_position: contextPosition,
      },
    )
    return response.data
  },

  async getQAHistory(moduleId: number, limit = 50): Promise<LearningQAListResponse> {
    const response = await apiClient.get<LearningQAListResponse>(
      `/api/v1/learn/modules/${moduleId}/questions`,
      { params: { limit } },
    )
    return response.data
  },

  // --- Books ---

  async listBooks(category?: BookCategory, offset = 0, limit = 50): Promise<BookListResponse> {
    const response = await apiClient.get<BookListResponse>('/api/v1/learn/books', {
      params: { category, offset, limit },
    })
    return response.data
  },

  async getBook(bookId: number): Promise<BookWithContent> {
    const response = await apiClient.get<BookWithContent>(`/api/v1/learn/books/${bookId}`)
    return response.data
  },

  async personalizeBook(bookId: number): Promise<{
    topic_id: number
    generation_status: string
    message: string
  }> {
    const response = await apiClient.post(`/api/v1/learn/books/${bookId}/personalize`)
    return response.data
  },

  // --- Notes ---

  async getNote(moduleId: number): Promise<LearningNote | null> {
    const response = await apiClient.get<LearningNote | null>(
      `/api/v1/learn/modules/${moduleId}/notes`,
    )
    return response.data
  },

  async saveNote(moduleId: number, content: string): Promise<LearningNote> {
    const response = await apiClient.put<LearningNote>(`/api/v1/learn/modules/${moduleId}/notes`, {
      content,
    })
    return response.data
  },

  async deleteNote(moduleId: number): Promise<void> {
    await apiClient.delete(`/api/v1/learn/modules/${moduleId}/notes`)
  },

  // --- Podcasts ---

  async listPodcasts(
    category?: PodcastCategory,
    offset = 0,
    limit = 50,
  ): Promise<PodcastListResponse> {
    const response = await apiClient.get<PodcastListResponse>('/api/v1/learn/podcasts', {
      params: { category, offset, limit },
    })
    return response.data
  },

  async getPodcast(podcastId: number): Promise<PodcastWithContent> {
    const response = await apiClient.get<PodcastWithContent>(`/api/v1/learn/podcasts/${podcastId}`)
    return response.data
  },
}
